import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, computed, watch } from "vue";
  import { useStore } from "vuex";
  import { AnalyticsDataItem } from "@/types/AnalyticsDataInterface";
  import { EstateInterface } from "@/types/EstateInterface";
  import { getAnalyticsData } from "@/helpers/analytics/getAnalyticsData";
  import getEstatesByLandowner from "@/helpers/analytics/getEstatesByLandowner";
  import { dataReferrer } from "@/constants/dataForAnalytics";
  import { ReportsEstateSelected } from "@/enums/ReportsEstateSelected";
  import { ChartData, TableData } from "@/types/AnalyticsReferrerInterface";
  import { ChartsTypes } from "@/enums/ChartsTypes";
  import MixedChart from "../reports/MixedChart.vue";
  import {
    optionsBarChartForLeadsReport,
    backgroundColorForLeadsReport,
    chartBackgroundColorForLeadsReport,
  } from "@/constants/optionsChart";
  import { useToast } from "vue-toastification";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ReferrerFromAnalytics',
  props: {
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
    searchKey: {
      type: Number,
      required: true,
    },
    estateSelected: {
      type: String,
      required: true,
    },
  },
  setup(__props, { expose: __expose }) {
  __expose();

  const props = __props;

  const loading = ref(false);
  const store = useStore();
  const toast = useToast();
  const DataFromAnalytics = ref<AnalyticsDataItem[]>([]);
  const estates = ref<EstateInterface[]>([]);

  const landownerId = computed(() => store.state.user.data._id);

  const startDateSelected = computed(() => {
    return props.startDate;
  });

  const endDateSelected = computed(() => {
    return props.endDate;
  });

  const estateSelected = computed(() => {
    return props.estateSelected;
  });

  watch(
    () => props.searchKey,
    () => {
      fetchDataFromAnalytics();
    }
  );
  const fetchDataFromAnalytics = async () => {
    await getAnalyticsData(
      startDateSelected.value,
      endDateSelected.value,
      dataReferrer,
      DataFromAnalytics,
      loading,
      store,
      toast
    );
  };

  const fetchEstatesByLandowner = () => {
    getEstatesByLandowner(landownerId.value, estates);
  };

  const estatesByLandownerSelected = computed(() => {
    if (estateSelected.value === ReportsEstateSelected.ALL_PROJECTS) {
      return estates.value;
    }

    return estates.value.filter(
      (estate) => estate.title === estateSelected.value
    );
  });

  const filteredDataFromAnalytics = computed(() => {
    if (!estatesByLandownerSelected.value || !DataFromAnalytics.value.length) {
      return [];
    }

    return DataFromAnalytics.value.filter((estateByAnalytics) => {
      const [dim0, dim1] = estateByAnalytics.dimensionValues;
      const estateFromAnalytics = dim0.value;

      const estateTitleExists = estatesByLandownerSelected.value.some(
        (estate) => estate.title === estateFromAnalytics
      );

      return (
        dim0.value &&
        dim0.value !== "(not set)" &&
        dim1.value &&
        dim1.value !== "(not set)" &&
        dim1.value !== "testing" &&
        estateTitleExists
      );
    });
  });

  const chartData = computed(() => {
    const sourceSet = new Set<string>();
    const estateSet = new Set<string>();
    const dataBySourceAndEstate: Record<string, Record<string, number>> = {};

    for (const dataForAnalytics of filteredDataFromAnalytics.value) {
      const [dim0, dim1] = dataForAnalytics.dimensionValues;
      const estate = dim0.value;
      const source = dim1.value;
      const count = Number(dataForAnalytics.metricValues[0].value);

      sourceSet.add(source);
      estateSet.add(estate);

      if (!dataBySourceAndEstate[source]) {
        dataBySourceAndEstate[source] = {};
      }

      dataBySourceAndEstate[source][estate] = count;
    }

    const labels = Array.from(estateSet);
    const datasets: ChartData[] = [];
    const backgroundColors = chartBackgroundColorForLeadsReport.slice(
      0,
      sourceSet.size
    );

    datasets.push(
      ...Array.from(sourceSet).map((source, index) => {
        const data: number[] = labels.map(
          (estate) => dataBySourceAndEstate[source][estate] || 0
        );

        return {
          type: ChartsTypes.BAR,
          label: source,
          data,
          backgroundColor: backgroundColors[index],
        };
      })
    );

    return {
      labels,
      datasets,
      source: Array.from(sourceSet),
    };
  });

  const tableData = computed<TableData[]>(() => {
    const estateSet = new Set<string>();
    const sourceSet = new Set<string>();
    const dataByEstate: Record<string, TableData> = {};

    for (const dataForAnalytics of filteredDataFromAnalytics.value) {
      const [dim0, dim1] = dataForAnalytics.dimensionValues;
      const estate = dim0.value;
      const source = dim1.value;
      const count = Number(dataForAnalytics.metricValues[0].value);

      estateSet.add(estate);
      sourceSet.add(source);

      if (!dataByEstate[estate]) {
        dataByEstate[estate] = {
          estate,
          count: Array.from(sourceSet).map(() => 0),
        };
      }

      const sourceIndex = Array.from(sourceSet).indexOf(source);

      dataByEstate[estate].count[sourceIndex] = count;
    }

    const estateArray = Array.from(estateSet);

    estateArray.forEach((estate) => {
      const countArray = dataByEstate[estate].count;
      const fullCountArray = Array.from(sourceSet).map((source) => {
        const sourceIndex = Array.from(sourceSet).indexOf(source);

        return countArray[sourceIndex] || 0;
      });

      dataByEstate[estate].count = fullCountArray;
    });

    return estateArray.map((estate) => {
      const countArray = dataByEstate[estate].count;

      return {
        estate,
        count: countArray,
      };
    });
  });

  const getTotalForSource = (source: string) => {
    let total = 0;

    for (const estate of tableData.value) {
      total += estate.count[chartData.value.source.indexOf(source)] || 0;
    }

    return total;
  };

  const calculateTotalForEstate = (estate: TableData) => {
    return estate.count.reduce((acc, count) => acc + count, 0);
  };

  const calculateGrandTotal = () => {
    return tableData.value.reduce((acc, estate) => {
      return acc + calculateTotalForEstate(estate);
    }, 0);
  };

  onMounted(() => {
    fetchDataFromAnalytics();
    fetchEstatesByLandowner();
  });

const __returned__ = { props, loading, store, toast, DataFromAnalytics, estates, landownerId, startDateSelected, endDateSelected, estateSelected, fetchDataFromAnalytics, fetchEstatesByLandowner, estatesByLandownerSelected, filteredDataFromAnalytics, chartData, tableData, getTotalForSource, calculateTotalForEstate, calculateGrandTotal, onMounted, ref, computed, watch, get useStore() { return useStore }, get AnalyticsDataItem() { return AnalyticsDataItem }, get EstateInterface() { return EstateInterface }, get getAnalyticsData() { return getAnalyticsData }, get getEstatesByLandowner() { return getEstatesByLandowner }, get dataReferrer() { return dataReferrer }, get ReportsEstateSelected() { return ReportsEstateSelected }, get ChartData() { return ChartData }, get TableData() { return TableData }, get ChartsTypes() { return ChartsTypes }, MixedChart, get optionsBarChartForLeadsReport() { return optionsBarChartForLeadsReport }, get backgroundColorForLeadsReport() { return backgroundColorForLeadsReport }, get chartBackgroundColorForLeadsReport() { return chartBackgroundColorForLeadsReport }, get useToast() { return useToast } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})